/**
 * Shared flow and label helpers for the team checkout experience.
 */
export enum TeamCheckoutSteps {
    CHECKOUT_OVERLAY = "checkoutOverlay",
    INVITE_USERS = "inviteUsers",
}

/**
 * The three team checkout entry paths (used by TeamCheckoutRedesignedView and layouts):
 * - REACTIVATE: User is reactivating a canceled subscription (seats fixed, no trial, contact support link).
 * - NEW_WITH_TRIAL: New workspace and user is eligible for trial (due today $0, trial end date, "Start Free Trial").
 * - NEW_NO_TRIAL: New workspace and user already has Pro/Team elsewhere ("Add Team Workspace", "Confirm", charge now).
 */
export type TeamCheckoutFlow = "reactivate" | "new_with_trial" | "new_no_trial";

export function getTeamCheckoutFlow(
    reactivateSubscription: boolean,
    hasProOrTeamSubscription: boolean
): TeamCheckoutFlow {
    if (reactivateSubscription) return "reactivate";
    if (hasProOrTeamSubscription) return "new_no_trial";
    return "new_with_trial";
}

export interface TeamCheckoutLabels {
    title: string;
    submitLabel: string;
    loadingState: string;
    subTitle: string | undefined;
}

export interface TeamCheckoutLabelOptions {
    /** When flow is reactivate, used for title: "Reactivate \"{{workspaceName}}\" subscription". */
    workspaceName?: string;
}

/** Labels for each checkout flow. For reactivate, pass workspaceName in labelOptions for a workspace-specific title. */
export function getTeamCheckoutLabels(
    flow: TeamCheckoutFlow,
    t: (key: string, options?: Record<string, unknown>) => string,
    labelOptions?: TeamCheckoutLabelOptions
): TeamCheckoutLabels {
    switch (flow) {
        case "reactivate": {
            const workspaceName = labelOptions?.workspaceName;
            return {
                title: workspaceName
                    ? t("Reactivate \"{{workspaceName}}\" subscription", { workspaceName })
                    : t("Reactivate subscription"),
                submitLabel: t("Reactivate"),
                loadingState: t("Reactivating subscription"),
                subTitle: undefined,
            };
        }
        case "new_no_trial":
            return {
                title: t("Add Team Workspace"),
                submitLabel: t("Confirm"),
                loadingState: t("Creating your workspace"),
                subTitle: undefined,
            };
        case "new_with_trial":
            return {
                title: t("Get started with our free trial"),
                submitLabel: t("Start Free Trial"),
                loadingState: t("Starting free trial"),
                subTitle: t("Cancel before your trial ends and  you won't be charged."),
            };
    }
}

